import ToolAvailability from './ToolAvailability';
import React, { useState } from 'react';
import {
  ChevronDown,
  ChevronRight,
  Clock3,
  MessageSquareMore,
  MessagesSquare,
  Pencil,
  Wrench,
} from 'lucide-react';

import ModernTimeDropdown from '../../../dashboard-components/layouts/modernDropdown';
import {
  CONVERSATION_CONTROL_INSTRUCTION_MAX_LENGTH,
  CONVERSATION_CONTROL_LIMITS,
  normalizeConversationControls,
} from './conversationControls.js';

const StandardToolsConfig = ({ value, onChange, pipelineType = 'stt_llm_tts', availableLanguages = [] }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [editingDraft, setEditingDraft] = useState(null);
  const [editingBaseline, setEditingBaseline] = useState(null);
  const [editorError, setEditorError] = useState('');
  const controls = normalizeConversationControls(value);
  const isVoiceToVoice = String(pipelineType || '').trim().toLowerCase() === 'voice_to_voice';

  const tools = [
    {
      key: 'user_wait_stop_signal',
      title: 'User Wait & Stop Signal',
      description: 'Lets the LLM silently pause when the customer says “wait”, “hold on”, or asks for a moment.',
      icon: Clock3,
      fieldKey: 'max_wait_seconds',
      fieldLabel: 'Default wait (seconds)',
      limits: CONVERSATION_CONTROL_LIMITS.user_wait_stop_signal,
      helpText: 'Enter 5–45 seconds. The LLM can override this value for one tool call. When omitted, this configured value is used.',
    },
    {
      key: 'long_user_input_handling',
      title: 'Long User Input Handling',
      description: 'Lets the LLM mark the next question as needing extra time across natural pauses.',
      icon: MessageSquareMore,
      fieldKey: 'continuation_timeout_seconds',
      fieldLabel: 'Default continuation timeout (seconds)',
      limits: CONVERSATION_CONTROL_LIMITS.long_user_input_handling,
      helpText: 'Enter 0.5–10 seconds. The LLM can override this value for one answer. Every resumed utterance restarts that turn’s timer.',
    },
    {
      key: 'accept_external_context',
      title: 'Accept external context',
      description: 'Allows selected external message sources to add user context during an active call.',
      icon: MessagesSquare,
    },
  ];

  const startEdit = (tool) => {
    const section = controls[tool.key];
    setEditingKey(tool.key);
    setEditingDraft(tool.key === 'accept_external_context' ? {
      enabled: section.enabled,
      sources: section.sources,
    } : {
      enabled: section.enabled,
      ...(section.availability ? { availability: section.availability } : {}),
      [tool.fieldKey]: String(section[tool.fieldKey]),
      instruction: section.instruction,
    });
    setEditingBaseline(JSON.stringify(section));
    setEditorError('');
  };

  const cancelEdit = () => {
    setEditingKey(null);
    setEditingDraft(null);
    setEditingBaseline(null);
    setEditorError('');
  };

  const updateDraft = (patch) => {
    setEditingDraft((current) => ({
      ...current,
      ...patch,
    }));
    setEditorError('');
  };

  const saveEdit = () => {
    const tool = tools.find((candidate) => candidate.key === editingKey);
    if (!tool || !editingDraft) return;

    if (JSON.stringify(controls[tool.key]) !== editingBaseline) {
      setEditorError('This tool configuration changed while you were editing. Reopen it to use the latest values.');
      return;
    }

    const isEnabled = editingDraft.enabled === 'Yes';

    if (tool.key === 'accept_external_context') {
      if (isEnabled && editingDraft.sources.length === 0) {
        setEditorError('Select at least one external context source.');
        return;
      }
      onChange({
        ...controls,
        [tool.key]: {
          ...controls[tool.key],
          enabled: isEnabled ? 'Yes' : 'No',
          sources: editingDraft.sources,
        },
      });
      cancelEdit();
      return;
    }

    const seconds = Number(editingDraft[tool.fieldKey]);
    const hasValidSeconds = Number.isFinite(seconds)
      && seconds >= tool.limits.minimum
      && seconds <= tool.limits.maximum;

    if (isEnabled && !hasValidSeconds) {
      setEditorError(
        `Enter a value between ${tool.limits.minimum} and ${tool.limits.maximum} seconds.`,
      );
      return;
    }

    onChange({
      ...controls,
      [tool.key]: {
        ...controls[tool.key],
        ...editingDraft,
        enabled: isEnabled ? 'Yes' : 'No',
        [tool.fieldKey]: hasValidSeconds ? seconds : controls[tool.key][tool.fieldKey],
      },
    });
    cancelEdit();
  };

  const editingTool = tools.find((tool) => tool.key === editingKey);
  const EditingIcon = editingTool?.icon;
  const draftEnabled = editingDraft?.enabled === 'Yes';

  return (
    <div className="border-t pt-6">
      <button
        type="button"
        className="w-full flex items-center justify-between text-left hover:bg-gray-50 p-2 rounded-lg transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
        aria-expanded={isExpanded}
      >
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            {isExpanded ? (
              <ChevronDown className="w-5 h-5 text-gray-600" />
            ) : (
              <ChevronRight className="w-5 h-5 text-gray-600" />
            )}
            <Wrench className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <h3 className="text-lg font-medium text-gray-900">Standard Tools</h3>
            <p className="text-sm text-gray-600">
              Configure optional conversation controls for your agent
            </p>
          </div>
        </div>
        <div className="text-sm text-gray-500">{tools.length} tools</div>
      </button>

      {isExpanded && (
        <div className="mt-4">
          <p className="text-sm text-gray-500 mb-4">
            Each tool is optional and independent. Disabled tools preserve the existing conversation behavior.
          </p>
          {isVoiceToVoice && (
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4">
              Voice-to-voice pipelines currently support User Wait &amp; Stop Signal only. Long User Input Handling
              requires an STT → LLM → TTS pipeline.
            </p>
          )}

          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tool
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {tools.map((tool) => {
                    const section = controls[tool.key];
                    const enabled = section.enabled === 'Yes';
                    const Icon = tool.icon;

                    return (
                      <tr key={tool.key} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3">
                          <div className="flex items-start gap-3">
                            <div className="p-1.5 rounded-md bg-indigo-50 text-indigo-600">
                              <Icon className="w-4 h-4" />
                            </div>
                            <div>
                              <div className="text-sm font-medium text-gray-900">{tool.title}</div>
                              <div className="text-xs text-gray-500 mt-0.5">{tool.description}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-xs">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full ${
                              enabled
                                ? 'bg-green-100 text-green-800'
                                : 'bg-gray-100 text-gray-700'
                            }`}
                          >
                            {enabled ? 'Enabled' : 'Disabled'}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <button
                            type="button"
                            className="px-2 py-1 text-gray-600 hover:text-indigo-700"
                            onClick={() => startEdit(tool)}
                            aria-label={`Edit ${tool.title}`}
                            title={`Edit ${tool.title}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {editingTool && EditingIcon && editingDraft && (
            <div className="mt-4 bg-white border border-gray-200 rounded-lg">
              <div className="p-4 grid grid-cols-1 gap-4 text-sm">
                {editingTool.key !== 'accept_external_context' && <ToolAvailability availableLanguages={availableLanguages} supportedModes={editingTool.key === 'user_wait_stop_signal' ? ['voice', 'chat'] : ['voice']} value={editingDraft.availability} onChange={availability => updateDraft({ availability })} />}
                <div className="flex items-start gap-3">
                  <div className="p-1.5 rounded-md bg-indigo-50 text-indigo-600">
                    <EditingIcon className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-900">Edit {editingTool.title}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{editingTool.description}</div>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Enabled
                    </label>
                    <ModernTimeDropdown
                      ariaLabel={`${editingTool.title} enabled`}
                      value={editingDraft.enabled}
                      onChange={(event) => updateDraft({ enabled: event.target.value })}
                      options={['Yes', 'No']}
                      width="w-full"
                    />
                  </div>

                  {draftEnabled && editingTool.fieldKey && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {editingTool.fieldLabel}
                      </label>
                      <input
                        aria-label={editingTool.fieldLabel}
                        type="number"
                        min={editingTool.limits.minimum}
                        max={editingTool.limits.maximum}
                        step={editingTool.limits.optionStep}
                        value={editingDraft[editingTool.fieldKey]}
                        onChange={(event) => updateDraft({
                          [editingTool.fieldKey]: event.target.value,
                        })}
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                      />
                      <p className="text-xs text-gray-400 mt-1">{editingTool.helpText}</p>
                    </div>
                  )}
                </div>

                {draftEnabled && editingTool.key === 'accept_external_context' && (
                  <div>
                    <div className="block text-sm font-medium text-gray-700 mb-2">
                      Sources
                    </div>
                    <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={editingDraft.sources.includes('whatsapp')}
                        onChange={(event) => updateDraft({
                          sources: event.target.checked ? ['whatsapp'] : [],
                        })}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      WhatsApp
                    </label>
                  </div>
                )}

                {draftEnabled && editingTool.fieldKey && (
                  <div>
                    <label
                      htmlFor={`${editingTool.key}-instruction`}
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Tool instruction
                    </label>
                    <textarea
                      id={`${editingTool.key}-instruction`}
                      aria-label={`${editingTool.title} instruction`}
                      value={editingDraft.instruction}
                      maxLength={CONVERSATION_CONTROL_INSTRUCTION_MAX_LENGTH}
                      rows={6}
                      onChange={(event) => updateDraft({ instruction: event.target.value })}
                      className="w-full resize-y rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    />
                    <div className="flex items-center justify-between gap-3 mt-1 text-xs text-gray-400">
                      <span>Leave blank to use the built-in instruction.</span>
                      <span>
                        {editingDraft.instruction.length.toLocaleString()} /{' '}
                        {CONVERSATION_CONTROL_INSTRUCTION_MAX_LENGTH.toLocaleString()}
                      </span>
                    </div>
                  </div>
                )}

                {editorError && (
                  <div
                    role="alert"
                    className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2"
                  >
                    {editorError}
                  </div>
                )}

                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={cancelEdit}
                    className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={saveEdit}
                    className="px-4 py-2 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white"
                  >
                    Save Tool
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default StandardToolsConfig;
